// services/workflow.service.js
import AuthService from './auth.service.js'
import logger from '../utils/logger';

/**
 * Workflow Service
 * Maneja todas las operaciones relacionadas con Workflows
 * Base URL configured via REACT_APP_WORKFLOW_API_URL
 * Execution API configured via REACT_APP_EXECUTION_API_URL
 */

const WORKFLOW_API_URL = process.env.REACT_APP_WORKFLOW_API_URL || 'http://localhost:8000';
const EXECUTION_API_URL = process.env.REACT_APP_EXECUTION_API_URL || 'https://run-dev.zenitailab.com';

class WorkflowService {
  constructor() {
    // Override the base URL for workflow API
    this.baseURL = WORKFLOW_API_URL;
    this.executionURL = EXECUTION_API_URL;
  }

  /**
   * Realizar request HTTP específico para workflow API con JWT
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del fetch
   * @returns {Promise<Object>} Respuesta de la API
   */
  async request(endpoint, options = {}) {
    // Use the full URL with workflow API base
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    
    // Get JWT token
    let token = AuthService.getAccessToken();
    
    // Check if token is expired
    if (token && AuthService.isTokenExpired()) {
      logger.warn('JWT token is expired. User needs to re-authenticate.');
      AuthService.logout();
      token = null;
    }
    
    // Prepare headers with JWT
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };
    
    // Add JWT if available and not expired
    if (token && !AuthService.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      logger.debug('JWT not added - token missing or expired');
    }
    
    const config = {
      ...options,
      headers,
    };

    try {
      logger.debug(`Workflow API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config);
      
      // Handle authentication errors
      if (response.status === 401) {
        logger.warn('Token expired or invalid for workflow API');
        AuthService.logout();
        
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        throw new Error('Session expired - redirecting to login');
      }

      // Parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorMessage = data.message || data.error || `HTTP Error ${response.status}`;
        throw new Error(errorMessage);
      }

      logger.api(config.method || 'GET', url, response.status);
      return data;
      
    } catch (error) {
      logger.error('Workflow API Error:', error.message);
      throw error;
    }
  }

  /**
   * Realizar request HTTP específico para Execution API con JWT
   * @param {string} endpoint - Endpoint de la API
   * @param {Object} options - Opciones del fetch
   * @returns {Promise<Object>} Respuesta de la API
   */
  async executionRequest(endpoint, options = {}) {
    // Use the full URL with execution API base
    const url = endpoint.startsWith('http') ? endpoint : `${this.executionURL}${endpoint}`;
    
    // Get JWT token
    let token = AuthService.getAccessToken();
    
    // Check if token is expired
    if (token && AuthService.isTokenExpired()) {
      logger.warn('JWT token is expired. User needs to re-authenticate.');
      AuthService.logout();
      token = null;
    }
    
    // Prepare headers with JWT
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    };
    
    // Add JWT if available and not expired
    if (token && !AuthService.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      logger.debug('JWT not added - token missing or expired');
    }
    
    const config = {
      ...options,
      headers,
    };

    try {
      logger.debug(`Execution API Request: ${config.method || 'GET'} ${url}`);
      
      const response = await fetch(url, config);
      
      // Handle authentication errors
      if (response.status === 401) {
        logger.warn('Token expired or invalid for execution API');
        AuthService.logout();
        
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        
        throw new Error('Session expired - redirecting to login');
      }

      // Parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = { message: await response.text() };
      }
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorMessage = data.message || data.error || `HTTP Error ${response.status}`;
        throw new Error(errorMessage);
      }

      logger.api(config.method || 'GET', url, response.status);
      return data;
      
    } catch (error) {
      logger.error('Execution API Error:', error.message);
      throw error;
    }
  }

  /**
   * List workflows with filtering and pagination - Protected endpoint
   * GET /workflows
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.status] - Status filter
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @param {string} [params.sort_by='updated_at'] - Sort by field
   * @param {boolean} [params.descending=true] - Sort order
   * @param {boolean} [params.include_definition=false] - Include workflow definition
   * @param {boolean} [params.include_soft_deleted=false] - Include soft deleted workflows
   * @returns {Promise<Object>} response { success, data: { workflows: [...], total_count, has_more }, error }
   */
  async listWorkflows({
    account_id,
    project_id,
    status = null,
    limit = 100,
    offset = 0,
    sort_by = 'updated_at',
    descending = true,
    include_definition = false,
    include_soft_deleted = false
  }) {
    try {
      if (!account_id) {
        return {
          success: false,
          error: 'account_id is required'
        };
      }

      if (!project_id) {
        return {
          success: false,
          error: 'project_id is required'
        };
      }

      const params = new URLSearchParams({
        account_id,
        project_id,
        limit: limit.toString(),
        offset: offset.toString(),
        sort_by,
        descending: descending.toString(),
        include_definition: include_definition.toString(),
        include_soft_deleted: include_soft_deleted.toString()
      });

      if (status) {
        params.append('status', status);
      }

      const response = await this.request(`/workflows/?${params}`);
      
      return {
        success: true,
        data: {
          workflows: response.body?.workflows || [],
          total_count: response.body?.total_count || 0,
          has_more: response.body?.has_more || false,
          request_id: response.body?.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing workflows:', error);
      
      // Handle specific error types
      let errorMessage = 'Error listing workflows';
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        errorMessage = 'Unable to connect to workflow service. Please check your connection or try again later.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error - workflow service may not be properly configured.';
      } else {
        errorMessage = error.message || errorMessage;
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Get a specific workflow by ID - Protected endpoint
   * GET /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required, UUID format)
   * @param {string} [project_id] - Project ID (optional)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async getWorkflow(workflow_id, project_id = null) {
    try {
      if (!workflow_id) {
        return {
          success: false,
          error: 'workflow_id is required'
        };
      }

      const params = new URLSearchParams();
      if (project_id) {
        params.append('project_id', project_id);
      }

      const queryString = params.toString();
      const endpoint = `/workflows/${workflow_id}${queryString ? `?${queryString}` : ''}`;

      const response = await this.request(endpoint);
      
      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          steps: response.body?.steps || [],
          triggers: response.body?.triggers || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting workflow:', error);
      return {
        success: false,
        error: error.message || 'Error getting workflow'
      };
    }
  }

  /**
   * Create a new workflow - Protected endpoint
   * POST /workflows/
   * @param {Object} workflowData - Workflow data
   * @param {string} workflowData.account_id - Account ID (required)
   * @param {string} workflowData.project_id - Project ID (required)
   * @param {string} workflowData.name - Workflow name (required)
   * @param {string} [workflowData.description] - Workflow description
   * @param {Object} [workflowData.definition] - Workflow definition
   * @param {Array} [workflowData.tags] - Tags for the workflow
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async createWorkflow(workflowData) {
    try {
      if (!workflowData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!workflowData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!workflowData.name) {
        return { success: false, error: 'name is required' };
      }

      const response = await this.request('/workflows/', {
        method: 'POST',
        body: JSON.stringify(this.sanitizeWorkflowData(workflowData))
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error creating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error creating workflow'
      };
    }
  }

  /**
   * Update an existing workflow - Protected endpoint
   * PUT /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} workflowData - Workflow data to update
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async updateWorkflow(workflow_id, workflowData) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}`, {
        method: 'PUT',
        body: JSON.stringify(this.sanitizeWorkflowData(workflowData))
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error updating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error updating workflow'
      };
    }
  }

  /**
   * Delete a workflow (soft delete) - Protected endpoint
   * DELETE /workflows/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, error }
   */
  async deleteWorkflow(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      await this.request(`/workflows/${workflow_id}`, {
        method: 'DELETE'
      });

      return { success: true };
    } catch (error) {
      console.error('WorkflowService: Error deleting workflow:', error);
      return {
        success: false,
        error: error.message || 'Error deleting workflow'
      };
    }
  }

  /**
   * Validate workflow definition - Protected endpoint
   * POST /workflows/{workflow_id}/validate
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { valid, errors }, error }
   */
  async validateWorkflow(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/validate`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          valid: response.body?.valid ?? response.valid ?? true,
          errors: response.body?.errors || response.errors || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error validating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error validating workflow'
      };
    }
  }

  /**
   * Activate workflow for execution - Protected endpoint
   * POST /workflows/{workflow_id}/activate
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} activateData - Activation data
   * @param {string} activateData.account_id - Account ID (required)
   * @param {string} activateData.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async activateWorkflow(workflow_id, activateData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!activateData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!activateData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        account_id: activateData.account_id,
        project_id: activateData.project_id,
        workflow_id: workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}/activate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error activating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error activating workflow'
      };
    }
  }

  /**
   * Deactivate workflow - Protected endpoint
   * POST /workflows/{workflow_id}/deactivate
   * @param {string} workflow_id - Workflow ID (required)
   * @param {Object} deactivateData - Deactivation data
   * @param {string} deactivateData.account_id - Account ID (required)
   * @param {string} deactivateData.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async deactivateWorkflow(workflow_id, deactivateData = {}) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!deactivateData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!deactivateData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        account_id: deactivateData.account_id,
        project_id: deactivateData.project_id,
        workflow_id: workflow_id
      };

      const response = await this.request(`/workflows/${workflow_id}/deactivate`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error deactivating workflow:', error);
      return {
        success: false,
        error: error.message || 'Error deactivating workflow'
      };
    }
  }

  /**
   * Restore a soft-deleted workflow - Protected endpoint
   * POST /workflows/{workflow_id}/restore
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { workflow }, error }
   */
  async restoreWorkflow(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/workflows/${workflow_id}/restore`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          workflow: response.body?.workflow || response.workflow || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error restoring workflow:', error);
      return {
        success: false,
        error: error.message || 'Error restoring workflow'
      };
    }
  }

  /**
   * Clean expired workflows (Admin only) - Protected endpoint
   * POST /workflows/admin/clean-expired
   * @returns {Promise<Object>} response { success, data: { cleaned_count }, error }
   */
  async cleanExpiredWorkflows() {
    try {
      const response = await this.request('/workflows/admin/clean-expired', {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          cleaned_count: response.body?.cleaned_count || response.cleaned_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error cleaning expired workflows:', error);
      return {
        success: false,
        error: error.message || 'Error cleaning expired workflows'
      };
    }
  }

  /**
   * Helper method to validate UUID format
   * @param {string} uuid - UUID string to validate
   * @returns {boolean} true if valid UUID format
   */
  isValidUUID(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  /**
   * Helper method to sanitize workflow data
   * @param {Object} workflow - Workflow data
   * @returns {Object} Sanitized workflow data
   */
  sanitizeWorkflowData(workflow) {
    if (!workflow) return workflow;

    // Basic sanitization - remove any unwanted fields
    const sanitized = { ...workflow };

    // Ensure proper data types
    if (sanitized.timeout_seconds && typeof sanitized.timeout_seconds !== 'number') {
      sanitized.timeout_seconds = parseInt(sanitized.timeout_seconds, 10);
    }

    if (sanitized.tags && !Array.isArray(sanitized.tags)) {
      sanitized.tags = [];
    }

    if (sanitized.steps && !Array.isArray(sanitized.steps)) {
      sanitized.steps = [];
    }

    return sanitized;
  }

  /**
   * Helper method to format workflow status for display
   * @param {string} status - Workflow status
   * @returns {Object} { label, color, bgColor }
   */
  formatStatus(status) {
    const statusMap = {
      'draft': {
        label: 'Draft',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-900/30'
      },
      'active': {
        label: 'Active',
        color: 'text-green-400',
        bgColor: 'bg-green-900/30'
      },
      'paused': {
        label: 'Paused',
        color: 'text-orange-400',
        bgColor: 'bg-orange-900/30'
      },
      'stopped': {
        label: 'Stopped',
        color: 'text-red-400',
        bgColor: 'bg-red-900/30'
      },
      'archived': {
        label: 'Archived',
        color: 'text-gray-400',
        bgColor: 'bg-gray-900/30'
      }
    };

    return statusMap[status] || {
      label: status || 'Unknown',
      color: 'text-gray-400',
      bgColor: 'bg-gray-900/30'
    };
  }

  /**
   * Helper method to format execution mode for display
   * @param {string} executionMode - Execution mode
   * @returns {string} Formatted execution mode
   */
  formatExecutionMode(executionMode) {
    const modeMap = {
      'sequential': 'Sequential',
      'parallel': 'Parallel',
      'conditional': 'Conditional'
    };

    return modeMap[executionMode] || executionMode || 'Unknown';
  }

  // ==========================================
  // TRIGGERS API
  // ==========================================

  /**
   * Create a new trigger - Protected endpoint
   * POST /triggers/
   * @param {Object} triggerData - Trigger data
   * @param {string} triggerData.workflow_id - Workflow ID (required)
   * @param {string} triggerData.trigger_type - Type: manual | scheduled | webhook | shopify | whatsapp
   * @param {string} [triggerData.name] - Trigger name
   * @param {Object} [triggerData.config] - Trigger configuration
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async createTrigger(triggerData) {
    try {
      if (!triggerData.workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }
      if (!triggerData.trigger_type) {
        return { success: false, error: 'trigger_type is required' };
      }

      const response = await this.request('/triggers/', {
        method: 'POST',
        body: JSON.stringify(triggerData)
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error creating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error creating trigger'
      };
    }
  }

  /**
   * Get a specific trigger by ID - Protected endpoint
   * GET /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async getTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}`);

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting trigger:', error);
      return {
        success: false,
        error: error.message || 'Error getting trigger'
      };
    }
  }

  /**
   * List triggers with filters - Protected endpoint
   * GET /triggers/
   * @param {Object} params - Query parameters
   * @param {string} [params.workflow_id] - Filter by workflow ID
   * @param {string} [params.trigger_type] - Filter by trigger type
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { triggers, total_count }, error }
   */
  async listTriggers({
    workflow_id = null,
    trigger_type = null,
    is_active = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (workflow_id) params.append('workflow_id', workflow_id);
      if (trigger_type) params.append('trigger_type', trigger_type);
      if (is_active !== null) params.append('is_active', is_active.toString());

      const response = await this.request(`/triggers/?${params}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing triggers:', error);
      return {
        success: false,
        error: error.message || 'Error listing triggers'
      };
    }
  }

  /**
   * List triggers for a specific workflow - Protected endpoint
   * GET /triggers/workflow/{workflow_id}
   * @param {string} workflow_id - Workflow ID (required)
   * @returns {Promise<Object>} response { success, data: { triggers }, error }
   */
  async listTriggersByWorkflow(workflow_id) {
    try {
      if (!workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const response = await this.request(`/triggers/workflow/${workflow_id}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing triggers by workflow:', error);
      return {
        success: false,
        error: error.message || 'Error listing triggers'
      };
    }
  }

  /**
   * Update a trigger - Protected endpoint
   * PUT /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @param {Object} triggerData - Trigger data to update
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async updateTrigger(trigger_id, triggerData) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}`, {
        method: 'PUT',
        body: JSON.stringify(triggerData)
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error updating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error updating trigger'
      };
    }
  }

  /**
   * Delete a trigger - Protected endpoint
   * DELETE /triggers/{trigger_id}
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, error }
   */
  async deleteTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      await this.request(`/triggers/${trigger_id}`, {
        method: 'DELETE'
      });

      return { success: true };
    } catch (error) {
      console.error('WorkflowService: Error deleting trigger:', error);
      return {
        success: false,
        error: error.message || 'Error deleting trigger'
      };
    }
  }

  /**
   * Activate a trigger - Protected endpoint
   * POST /triggers/{trigger_id}/activate
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async activateTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}/activate`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error activating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error activating trigger'
      };
    }
  }

  /**
   * Deactivate a trigger - Protected endpoint
   * POST /triggers/{trigger_id}/deactivate
   * @param {string} trigger_id - Trigger ID (required)
   * @returns {Promise<Object>} response { success, data: { trigger }, error }
   */
  async deactivateTrigger(trigger_id) {
    try {
      if (!trigger_id) {
        return { success: false, error: 'trigger_id is required' };
      }

      const response = await this.request(`/triggers/${trigger_id}/deactivate`, {
        method: 'POST'
      });

      return {
        success: true,
        data: {
          trigger: response.body?.trigger || response.trigger || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error deactivating trigger:', error);
      return {
        success: false,
        error: error.message || 'Error deactivating trigger'
      };
    }
  }

  /**
   * List Shopify triggers - Protected endpoint
   * GET /triggers/shopify/list
   * @param {Object} params - Query parameters
   * @param {string} [params.shop_domain] - Shopify shop domain
   * @param {string} [params.event_type] - Event type (orders/create, products/update, etc.)
   * @param {boolean} [params.is_active] - Filter by active status
   * @param {number} [params.limit=100] - Limit for pagination
   * @param {number} [params.offset=0] - Offset for pagination
   * @returns {Promise<Object>} response { success, data: { triggers, total_count }, error }
   */
  async listShopifyTriggers({
    shop_domain = null,
    event_type = null,
    is_active = null,
    limit = 100,
    offset = 0
  } = {}) {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });

      if (shop_domain) params.append('shop_domain', shop_domain);
      if (event_type) params.append('event_type', event_type);
      if (is_active !== null) params.append('is_active', is_active.toString());

      const response = await this.request(`/triggers/shopify/list?${params}`);

      return {
        success: true,
        data: {
          triggers: response.body?.triggers || response.triggers || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing Shopify triggers:', error);
      return {
        success: false,
        error: error.message || 'Error listing Shopify triggers'
      };
    }
  }

  // ============================================================================
  // EXECUTION ENDPOINTS
  // ============================================================================

  /**
   * Start a workflow execution
   * POST /executions/
   * @param {Object} executionData - Execution parameters
   * @param {string} executionData.account_id - Account ID (required)
   * @param {string} executionData.project_id - Project ID (required)
   * @param {string} executionData.workflow_id - Workflow ID to execute (required)
   * @param {Object} [executionData.input_parameters] - Input parameters for execution
   * @param {string} [executionData.trigger_type='manual'] - Type of trigger
   * @param {string} [executionData.triggered_by_user_id] - User who triggered
   * @param {Object} [executionData.metadata] - Optional execution metadata
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async startExecution(executionData) {
    try {
      if (!executionData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!executionData.project_id) {
        return { success: false, error: 'project_id is required' };
      }
      if (!executionData.workflow_id) {
        return { success: false, error: 'workflow_id is required' };
      }

      const payload = {
        id: executionData.id || crypto.randomUUID(),
        account_id: executionData.account_id,
        project_id: executionData.project_id,
        workflow_id: executionData.workflow_id,
        input_parameters: executionData.input_parameters || {},
        trigger_type: executionData.trigger_type || 'manual',
        triggered_by_user_id: executionData.triggered_by_user_id || null,
        metadata: executionData.metadata || null
      };

      const response = await this.executionRequest('/executions/', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          execution: response.body?.execution || response.execution || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error starting execution:', error);
      return {
        success: false,
        error: error.message || 'Error starting workflow execution'
      };
    }
  }

  /**
   * List workflow executions with filtering
   * GET /executions/
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.workflow_id] - Filter by workflow
   * @param {Array<string>} [params.status_filter] - Filter by status
   * @param {Array<string>} [params.trigger_type_filter] - Filter by trigger type
   * @param {string} [params.start_date] - Filter after date
   * @param {string} [params.end_date] - Filter before date
   * @param {number} [params.limit=50] - Max results (1-100)
   * @param {number} [params.offset=0] - Skip results
   * @param {string} [params.sort_by='started_at'] - Sort field
   * @param {string} [params.sort_order='desc'] - Sort direction
   * @returns {Promise<Object>} response { success, data: { executions, total_count }, error }
   */
  async listExecutions(params = {}) {
    try {
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      if (params.workflow_id) queryParams.append('workflow_id', params.workflow_id);
      if (params.status_filter?.length) {
        params.status_filter.forEach(s => queryParams.append('status_filter', s));
      }
      if (params.trigger_type_filter?.length) {
        params.trigger_type_filter.forEach(t => queryParams.append('trigger_type_filter', t));
      }
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);

      const response = await this.executionRequest(`/executions/?${queryParams}`);

      return {
        success: true,
        data: {
          executions: response.body?.executions || response.executions || [],
          total_count: response.body?.total_count || response.total_count || 0,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error listing executions:', error);
      return {
        success: false,
        error: error.message || 'Error listing executions'
      };
    }
  }

  /**
   * Get execution details
   * GET /executions/{execution_id}
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {boolean} [params.include_step_results=true] - Include step results
   * @param {boolean} [params.include_logs_summary=true] - Include logs summary
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async getExecution(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      if (params.include_step_results !== undefined) {
        queryParams.append('include_step_results', params.include_step_results.toString());
      }
      if (params.include_logs_summary !== undefined) {
        queryParams.append('include_logs_summary', params.include_logs_summary.toString());
      }

      const response = await this.executionRequest(`/executions/${execution_id}?${queryParams}`);

      return {
        success: true,
        data: {
          execution: response.body?.execution || response.execution || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution details'
      };
    }
  }

  /**
   * Get execution status
   * GET /executions/{execution_id}/status
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @returns {Promise<Object>} response { success, data: { status, progress }, error }
   */
  async getExecutionStatus(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      const response = await this.executionRequest(`/executions/${execution_id}/status?${queryParams}`);

      return {
        success: true,
        data: {
          status: response.body?.status || response.status,
          progress: response.body?.progress || response.progress,
          execution: response.body?.execution || response.execution || response,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution status:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution status'
      };
    }
  }

  /**
   * Get execution logs
   * GET /executions/{execution_id}/logs
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} params - Query parameters
   * @param {string} params.account_id - Account ID (required)
   * @param {string} params.project_id - Project ID (required)
   * @param {string} [params.log_level] - Filter by log level
   * @param {string} [params.source] - Filter by log source
   * @param {string} [params.start_time] - Filter after time
   * @param {string} [params.end_time] - Filter before time
   * @param {number} [params.limit=100] - Max logs (1-1000)
   * @param {number} [params.offset=0] - Skip logs
   * @param {string} [params.sort_by='timestamp'] - Sort field
   * @param {boolean} [params.descending=true] - Sort descending
   * @returns {Promise<Object>} response { success, data: { logs, total_count }, error }
   */
  async getExecutionLogs(execution_id, params = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!params.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!params.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const queryParams = new URLSearchParams({
        account_id: params.account_id,
        project_id: params.project_id
      });

      if (params.log_level) queryParams.append('log_level', params.log_level);
      if (params.source) queryParams.append('source', params.source);
      if (params.start_time) queryParams.append('start_time', params.start_time);
      if (params.end_time) queryParams.append('end_time', params.end_time);
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.offset) queryParams.append('offset', params.offset.toString());
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.descending !== undefined) queryParams.append('descending', params.descending.toString());

      const response = await this.executionRequest(`/executions/${execution_id}/logs?${queryParams}`);

      // Parse the response body if it's a string
      const responseBody = typeof response.body === 'string' 
        ? JSON.parse(response.body) 
        : response.body || response;

      return {
        success: true,
        data: {
          logs: responseBody.logs || [],
          total_count: responseBody.total_count || 0,
          request_id: responseBody.request_id,
          execution: responseBody.execution,
          workflow: responseBody.workflow
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error getting execution logs:', error);
      return {
        success: false,
        error: error.message || 'Error getting execution logs'
      };
    }
  }

  /**
   * Cancel a running execution
   * POST /executions/{execution_id}/cancel
   * @param {string} execution_id - Execution ID (required)
   * @param {Object} cancelData - Cancel parameters
   * @param {string} cancelData.account_id - Account ID (required)
   * @param {string} cancelData.project_id - Project ID (required)
   * @param {string} [cancelData.reason] - Reason for cancellation
   * @param {boolean} [cancelData.refund_coins=true] - Whether to refund coins
   * @returns {Promise<Object>} response { success, data: { execution }, error }
   */
  async cancelExecution(execution_id, cancelData = {}) {
    try {
      if (!execution_id) {
        return { success: false, error: 'execution_id is required' };
      }
      if (!cancelData.account_id) {
        return { success: false, error: 'account_id is required' };
      }
      if (!cancelData.project_id) {
        return { success: false, error: 'project_id is required' };
      }

      const payload = {
        id: cancelData.id || crypto.randomUUID(),
        account_id: cancelData.account_id,
        project_id: cancelData.project_id,
        execution_id: execution_id,
        reason: cancelData.reason || null,
        refund_coins: cancelData.refund_coins !== false // default true
      };

      const response = await this.executionRequest(`/executions/${execution_id}/cancel`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      return {
        success: true,
        data: {
          execution: response.body?.execution || response.execution || response,
          refunded: response.body?.refunded || response.refunded,
          request_id: response.body?.request_id || response.request_id
        }
      };
    } catch (error) {
      console.error('WorkflowService: Error cancelling execution:', error);
      return {
        success: false,
        error: error.message || 'Error cancelling execution'
      };
    }
  }

  /**
   * Helper to format execution status for display
   * @param {string} status - Execution status
   * @returns {Object} { label, icon, color, bgColor }
   */
  formatExecutionStatus(status) {
    const statusMap = {
      'pending': { label: 'Pending', icon: '⏳', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' },
      'running': { label: 'Running', icon: '▶️', color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
      'completed': { label: 'Completed', icon: '✅', color: 'text-green-400', bgColor: 'bg-green-400/10' },
      'failed': { label: 'Failed', icon: '❌', color: 'text-red-400', bgColor: 'bg-red-400/10' },
      'cancelled': { label: 'Cancelled', icon: '🚫', color: 'text-gray-400', bgColor: 'bg-gray-400/10' },
      'timeout': { label: 'Timeout', icon: '⏰', color: 'text-orange-400', bgColor: 'bg-orange-400/10' }
    };

    return statusMap[status?.toLowerCase()] || { 
      label: status || 'Unknown', 
      icon: '❓', 
      color: 'text-gray-400', 
      bgColor: 'bg-gray-400/10' 
    };
  }

  /**
   * Helper to format trigger type for display
   * @param {string} triggerType - Trigger type
   * @returns {Object} { label, icon, color }
   */
  formatTriggerType(triggerType) {
    const typeMap = {
      'manual': { label: 'Manual', icon: '🖱️', color: 'text-blue-400' },
      'scheduled': { label: 'Scheduled', icon: '⏰', color: 'text-purple-400' },
      'webhook': { label: 'Webhook', icon: '🔗', color: 'text-green-400' },
      'shopify': { label: 'Shopify', icon: '🛍️', color: 'text-emerald-400' },
      'whatsapp': { label: 'WhatsApp', icon: '💬', color: 'text-green-500' }
    };

    return typeMap[triggerType] || { label: triggerType || 'Unknown', icon: '❓', color: 'text-gray-400' };
  }
}

const workflowService = new WorkflowService();
export default workflowService;